/**
 * AIMDv2 – lesson media helpers (stage 6 "Content").
 *
 * AI-generated lesson videos are appended into `lesson.content` as raw HTML
 * (see ContentStep's video polling → appendMediaToLesson). A lesson-level
 * "Improve with AI" replaces `lesson.content` wholesale with the regenerated
 * prose, which used to silently drop any video the admin had already added.
 * These helpers let the regenerate path carry the video across.
 */

/** Wrapper appendMediaToLesson uses so media always lands as a block element. */
const MEDIA_WRAPPER_OPEN = '<div style="margin:16px 0 0">';

// Matches a <video …>…</video> pair or a self-closing/void <video … />.
const VIDEO_TAG_RE = /<video\b[^>]*>[\s\S]*?<\/video>|<video\b[^>]*\/?>/gi;

/** Every <video> tag in the lesson HTML, in document order. */
export function extractVideoTags(html: string | null | undefined): string[] {
  if (!html) return [];
  return html.match(VIDEO_TAG_RE) || [];
}

/** True when the lesson content carries at least one video. */
export function hasLessonVideo(html: string | null | undefined): boolean {
  return extractVideoTags(html).length > 0;
}

/** `src` of a <video> tag, or '' when it has none. */
function videoSrc(tag: string): string {
  const m = tag.match(/\bsrc=["']([^"']+)["']/i);
  return m ? m[1] : '';
}

/**
 * Re-append videos that existed before a regenerate but are missing from the
 * regenerated content. Videos the new content already carries (matched on
 * `src`) are left alone so nothing is duplicated.
 */
export function preserveVideos(previousHtml: string | null | undefined, nextHtml: string): string {
  const carried = extractVideoTags(previousHtml).filter((tag) => {
    const src = videoSrc(tag);
    // No src to match on — keep it rather than risk losing the admin's video.
    return src ? !nextHtml.includes(src) : true;
  });
  if (carried.length === 0) return nextHtml;
  return (
    nextHtml.trimEnd() + carried.map((tag) => `${MEDIA_WRAPPER_OPEN}${tag}</div>`).join('')
  );
}
